<template>
  <div
    class="MenuPricingDetails"
    :class="{ SingleLine: singleLine || selectable }">
    <template
      v-if="customised">
      <div class="CustomisedContentBefore">
        <div class="MenuPricingName">
          {{ menuConfig.name }}
        </div>
        <div class="MenuPricingSummary">
          {{ menuConfig.summary }}
        </div>
        <div class="MenuPricingAdditionalSummary">
          {{ menuConfig.additionalSummary }}
        </div>
      </div>
      <div
        v-if="beforeEarlyBirdDate"
        class="EarlyBirdBlock">
        <div class="MenuPriceHeader">
          Orders by Jan 20 (Mon)
        </div>
      </div>
    </template>
    <label
      :for="selectable ? `menu_config_${menuConfig.id}` : null"
      :class="{ Selectable: selectable }">
      <div :class="{ MenuConfigPriceWrapper: singleLine || selectable }">
        <input
          v-if="selectable"
          :id="`menu_config_${menuConfig.id}`"
          :checked="selectedMenuPricing.id === menuConfig.id"
          type="radio"
          class="Radio"
          name="selected_menu_config"
          :value="menuConfig"
          @change="onChangeMenuPricing(menuConfig)">
        <div class="MenuConfigPrice">
          <div
            v-if="menuConfig.pricePrefix"
            class="Price price-prefix">
            {{ menuConfig.pricePrefix }}
          </div>
          <span class="Price">
            <template v-if="isChristmasEarlyBirdPrice">
              <span
                v-if="!selectable"
                class="strikethrough">{{ $t('common.price', { price: christmasRegularPrice.toFixed(2) }) }}</span>
              {{ $t('common.price', { price }) }}
            </template>
            <template v-else-if="menuConfig.menuId === 95 && beforeEarlyBirdDate">
              <span
                v-if="!selectable"
                class="strikethrough">{{ $t('common.price', { price: nonEarlyBirdPrice }) }}</span>
              {{ $t('common.price', { price }) }}
            </template>
            <template v-else-if="menuConfig.menuId === 96 && beforeEarlyBirdDate">
              <span
                v-if="!selectable"
                class="strikethrough">{{ $t('common.price', { price: nonEarlyBirdPrice }) }}</span>
              {{ $t('common.price', { price }) }}
            </template>
            <template v-else-if="menuConfig.menuId === 42 && beforeEarlyBirdDate">
              <span
                v-if="!selectable"
                class="strikethrough">{{ $t('common.price', { price: nonEarlyBirdPrice }) }}</span>
              {{ $t('common.price', { price }) }}
            </template>
            <template v-else-if="menuConfig.menuId === 96 && beforeEarlyBirdDate">
              <span
                v-if="!selectable"
                class="strikethrough">{{ $t('common.price', { price: nonEarlyBirdPrice }) }}</span>
              {{ $t('common.price', { price }) }}
            </template>
            <template v-else>
              {{ $t('common.price', { price }) }}
            </template>
          </span>
          <span
            v-if="priceSuffix"
            class="PerGuest"> /{{ priceSuffix }} </span>
          <br>
          <span>
            {{ $t('common.priceWithGst', { priceWithGst }) }}
          </span>
        </div>
      </div>
      <div
        v-if="showDetails"
        class="MenuConfigDetails">
        <div class="MenuConfigSummary">
          {{ summaryCopy }}
        </div>
        <div class="MenuConfigMinimumPax">
          {{ minimumPaxCopy }}
        </div>
      </div>
      <div
        v-if="customised"
        class="CustomisedContentAfter">
        <div v-if="beforeEarlyBirdDate">
          <div class="MenuPriceHeader">Jan 21 (Tue) onwards</div>
          <div class="MenuConfigPrice">
            <div
              v-if="menuConfig.pricePrefix"
              class="Price price-prefix">
              {{ menuConfig.pricePrefix }}
            </div>
            <span class="Price">
              {{ $t('common.price', { price: standardPrice }) }}
            </span>
            <span
              v-if="priceSuffix"
              class="PerGuest"> /{{ priceSuffix }} </span>
            <br>
            <span>
              {{ $t('common.priceWithGst', { priceWithGst: standardPriceWithGst }) }}
            </span>
          </div>
        </div>
      </div>
      <div
        v-if="isHariRayaMenu && customised"
        class="CustomisedContentAfter">
        <div class="MenuConfigPrice">
          <span class="Price">
            {{ $t('common.price', { price: menuConfig.miniBuffetPricePerPax }) }}
          </span>
          <span
            v-if="priceSuffix"
            class="PerGuest"> /{{ priceSuffix }} </span>
          <br>
          <span>
            {{ $t('common.priceWithGst', { priceWithGst: miniBuffetPriceWithGst }) }}
          </span>
        </div>
      </div>
    </label>
  </div>
</template>

<script>
import { CHRISTMAS_REGULAR_PRICE_BY_EARLY_BIRD_PRICE, MENU_IDS, getTaxRate } from '@/assets/js/order-helper'
import { isBeforeChristmasEarlyBirdPeriod, isBeforeEarlyBirdPeriod } from '@/assets/js/highlight-menu-helper'
import { mapGetters } from 'vuex'

export default {
  name: 'menu-pricing-details',
  props: {
    menuConfig: {
      type: Object,
      required: true
    },
    selectable: {
      type: Boolean,
      default: false
    },
    selectedMenuPricing: {
      type: Object,
      default: null
    },
    singleLine: {
      type: Boolean,
      default: false
    },
    showDetails: {
      type: Boolean,
      default: false
    },
    customised: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapGetters([
      'getActiveOrder'
    ]),
    isHariRayaMenu() {
      return [100, 101].includes(this.menuConfig.menuId)
    },
    priceToUse() {
      if (this.menuConfig.isFixedPax) {
        return this.menuConfig.pricePerPax * this.menuConfig.minimumPax
      }
      return this.menuConfig.pricePerPax
    },
    price() {
      return parseFloat(this.priceToUse).toFixed(2)
    },
    nonEarlyBirdPrice() {
      // CNY Gathering Box (42) adds 40, CNY menus (95, 96) add 5, Christmas menus (91, 92) add 4
      let priceIncrease = 4
      if (this.menuConfig.menuId === 42) {
        priceIncrease = 40
      } else if ([95, 96].includes(this.menuConfig.menuId)) {
        priceIncrease = 5
      }
      return (parseFloat(this.priceToUse) + priceIncrease).toFixed(2)
    },
    priceWithGst() {
      // show tax based on current day if path is /catering
      const taxRate = getTaxRate((this.$route.path !== '/catering' && this.getActiveOrder) ? this.getActiveOrder.selectedDate : null)
      return (this.priceToUse * (1 + taxRate)).toFixed(2)
    },
    miniBuffetPriceWithGst() {
      const taxRate = getTaxRate((this.$route.path !== '/catering' && this.getActiveOrder) ? this.getActiveOrder.selectedDate : null)
      return (parseFloat(this.menuConfig.miniBuffetPricePerPax) * (1 + taxRate)).toFixed(2)
    },
    priceSuffix() {
      if (this.menuConfig.isFixedPax) {
        return this.$t('menuPricingDetails.suffix.set')
      }
      if (this.menuConfig.priceSuffix !== '') {
        return this.menuConfig.priceSuffix || this.$t('menuPricingDetails.suffix.guest')
      }
      return null
    },
    standardPrice() {
      return parseFloat(this.menuConfig.standardPricePerPax).toFixed(2)
    },
    standardPriceWithGst() {
      // show tax based on current day if path is /catering
      const taxRate = getTaxRate((this.$route.path !== '/catering' && this.getActiveOrder) ? this.getActiveOrder.selectedDate : null)
      return (this.menuConfig.standardPricePerPax * (1 + taxRate)).toFixed(2)
    },
    minimumPaxCopy() {
      let count = this.menuConfig.minimumPax
      // HACK: this is a hard-coded feature
      // 48: Party Set Menu
      // 50: Chinese New Year Party Set
      // 59: Christmas Party Set Menu
      switch (this.menuConfig.menuId) {
        case 48:
        case 50:
          switch (this.menuConfig.minimumPax) {
            case 10:
              count = '8–10'
              break
            case 5:
              count = '4–6'
              break
          }
          break
        case 59:
          switch (this.menuConfig.minimumPax) {
            case 8:
              count = '6–8'
              break
            case 4:
              count = '3–5'
              break
          }
          break
      }
      return this.$t(`menuPricingDetails.${this.menuConfig.isFixedPax ? 'fixedPax' : 'minimumPax'}`, { count })
    },
    summaryCopy() {
      if (this.menuConfig.menuId === 106) {
        switch (this.menuConfig.id) {
          case 252:
            return '7 courses, 1 drink'
          case 253:
          case 254:
            return '8 courses, 1 drink'
          default:
            return this.menuConfig.summary
        }
      }

      // Check if menuId is 95 (CNY 2026 Buffet)
      if (this.menuConfig.menuId === 95) {
        const price = this.menuConfig.pricePerPax.toString()
        const isBeforeEarlyBird = isBeforeEarlyBirdPeriod()

        if (isBeforeEarlyBird) {
          // Before early bird ends
          switch (price) {
            case '33.88':
              return '7 courses'
            case '38.88':
              return '8 courses'
            case '58.88':
              return '9 courses'
            default:
              return this.menuConfig.summary
          }
        } else {
          // After early bird ends
          switch (price) {
            case '38.88':
              return '7 courses'
            case '43.88':
              return '8 courses'
            case '63.88':
              return '9 courses'
            default:
              return this.menuConfig.summary
          }
        }
      }

      // Check if menuId is 96 (CNY 2026 Mini Buffet)
      if (this.menuConfig.menuId === 96) {
        const price = this.menuConfig.pricePerPax.toString()
        const isBeforeEarlyBird = isBeforeEarlyBirdPeriod()

        if (isBeforeEarlyBird) {
          // Before early bird ends
          switch (price) {
            case '32.88':
              return '6 courses'
            case '37.88':
              return '7 courses'
            case '57.88':
              return '8 courses'
            default:
              return this.menuConfig.summary
          }
        } else {
          // After early bird ends
          switch (price) {
            case '37.88':
              return '6 courses'
            case '42.88':
              return '7 courses'
            case '62.88':
              return '8 courses'
            default:
              return this.menuConfig.summary
          }
        }
      }

      // Check if menuId is 5 (Classic Buffet)
      if (this.menuConfig.menuId === 5) {
        const price = this.menuConfig.pricePerPax.toString()
        switch (price) {
          case '18.8':
            return '7 courses'
          case '21.8':
            return '8 courses'
          case '24.8':
            return '9 courses'
          case '31.8':
            return '10 courses'
          default:
            return this.menuConfig.summary
        }
      }

      return this.menuConfig.summary
    },
    beforeEarlyBirdDate() {
      return isBeforeEarlyBirdPeriod()
    },
    christmasRegularPrice() {
      if (![MENU_IDS.christmasBuffet, MENU_IDS.christmasMiniBuffet].includes(this.menuConfig.menuId)) {
        return undefined
      }
      return CHRISTMAS_REGULAR_PRICE_BY_EARLY_BIRD_PRICE[parseFloat(this.priceToUse)]
    },
    isChristmasEarlyBirdPrice() {
      // A tier ops has already moved to its regular price no longer matches an early bird price,
      // so it shows a single price.
      return this.christmasRegularPrice !== undefined && isBeforeChristmasEarlyBirdPeriod()
    }
  },
  methods: {
    onChangeMenuPricing(menuPricing) {
      this.$emit('change', menuPricing)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/assets/css/base';

@import '~@/assets/css/_shared_variables.sass';

.MenuPricingDetails {
  &.SingleLine {
    margin-bottom: 0.75rem;

    .MenuConfigPriceWrapper,
    .MenuConfigDetails {
      display: inline-block;
      width: 50%;
      vertical-align: middle;
    }

    .MenuConfigPriceWrapper {
      .Radio {
        top: 5px;
        margin-right: 0.5rem;
      }

      .MenuConfigPrice {
        display: inline-block;
        vertical-align: top;
      }
    }

    .MenuConfigDetails {
      margin-top: 0;
      text-align: right;

      .MenuConfigSummary {
        margin-bottom: 0.25rem;
      }

      .MenuConfigMinimumPax {
        color: $gray-dark;
      }
    }
  }

  .Selectable {
    cursor: pointer;
  }

  .MenuConfigPrice {
    font-size: 12px;
    @extend %bold_weight;
    color: $ink;
    white-space: nowrap;

    .Price {
      @extend %display_pricing;
    }

    .strikethrough {
      text-decoration: line-through;
    }

    .PerGuest {
      position: relative;
      top: $space-xxs;
      vertical-align: top;
    }
  }

  .MenuConfigDetails {
    margin-top: 1rem;
    font-size: 12px;
    font-weight: bold;
    color: $ink;
  }

  .MenuPricingName {
    @extend %display_medium;
    color: $ink;
  }

  .MenuPricingSummary,
  .MenuPricingAdditionalSummary {
    @extend %body;
    font-weight: normal;
    color: $gray-darker;
  }

  .MenuPricingSummary {
    margin-top: 0.25rem;
  }

  .MenuPricingAdditionalSummary {
    padding-bottom: $space-l;

    @media #{$medium-up} {
      padding-bottom: $space-xl;
    }
  }

  .MenuPriceHeader {
    @extend %body;
    @extend %bold_weight;
  }

  .CustomisedContentAfter {
    margin-top: $space-l;
  }
}

.Cny {
  .MenuPricingDetails {
    .MenuPriceHeader {
      padding-bottom: $space-m;
    }
    .MenuPricingAdditionalSummary {
      padding-bottom: $space-m !important;

      @media #{$medium-up} {
        padding-bottom: $space-l !important;
      }
    }
  }
}

.EarlyBirdBlock {
  margin-top: 0;

  @media #{$medium-up} {
    margin-top: $space-l;
  }
}
</style>
